import React, { useState } from "react";
import { snakeToTitleCase, toCurrency, toDays, toTitleCase, toUpperCase } from "services/employer.service";
import { Vacancy } from "services/types";
import DateLabel from "./date-label.component";
import PortalModal from "./portal-modal.component";
import { getFrontendPortalUrl } from "services/config.service";

interface VacancyCardProps {
  vacancy: Vacancy
}

const VacancyCard: React.FC<VacancyCardProps> = ({ vacancy }) => {
  const [selectedVacancy, setSelectedVacancy] = useState<Vacancy>();
  const [show, setShow] = useState<boolean>(false);

  return (
    <>
      <div className="col-lg-4 col-md-6" key={vacancy.ref_id}>
        <div className="job-card">
          <div className="job-header">
            <i className="bi bi-gear-wide-connected"></i>
            <h4>{vacancy.job_type.name}, {toTitleCase(vacancy.rank.name)}</h4>
          </div>
          <p><i className="bi bi-building"></i> {vacancy.agency?.name}</p>
          <p><i className="bi bi-briefcase"></i> Experience: {vacancy.exp[0]} - {vacancy.exp[1]} year(s)</p>
          <p><i className="bi bi-calendar"></i> Posted: <DateLabel created_at={vacancy.created_at} /></p>
          <div className="d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-indigo"
              onClick={() => {
                setSelectedVacancy(vacancy);
                setShow(true);
              }}
            >
              View Detail
            </button>
            <a
              href={`${getFrontendPortalUrl()}/#/verify-email?action=apply-vacancy&job_ref_id=${vacancy.ref_id}`}
              className="btn btn-indigo"
            >
              Apply Now
            </a>
          </div>
        </div>
      </div>
      <PortalModal size="xl" show={show} onClose={() => setShow(false)} title="Job Details" >
        <div className="job-details-form row g-4">
          <Detail label="Company Name" value={selectedVacancy?.agency.name} />
          <Detail label="PIC" value={selectedVacancy?.requested_by} />
          <Detail
            label="Mobile Number"
            value={`${selectedVacancy?.isd_code} - ${selectedVacancy?.contact_no}`}
            highlight
          />
          <Detail
            label="Required Rank"
            value={toUpperCase(selectedVacancy?.rank.name)}
          />
          <Detail
            label="Type of Vessel"
            value={toUpperCase(selectedVacancy?.vessel_type?.name)}
          />
          <Detail
            label="Trading Area"
            value={toUpperCase(selectedVacancy?.area_of_operation?.name)}
          />
          <Detail
            label="Day Rate"
            value={toCurrency(selectedVacancy?.day_rate)}
            highlight
          />
          <Detail
            label="Contract Duration"
            value={toDays(selectedVacancy?.rotation)}
          />
          <Detail
            label="Contract Type"
            value={snakeToTitleCase(selectedVacancy?.contract_type)}
          />
          <Detail
            label="Approx. Joining Date"
            value={selectedVacancy?.requirement_date}
          />
          <Detail
            label="Advertisement Date"
            value={selectedVacancy?.created_at}
          />
        </div>
      </PortalModal>

      {/* INLINE CSS */}
      <style>{`
        .job-card {
          background: #ffffff;
          border-radius: 10px;
          padding: 18px;
          box-shadow: 0 4px 14px rgba(0,0,0,0.08);
          transition: transform 0.2s ease;
        }

        .job-card:hover {
          transform: translateY(-4px);
        }

        .job-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }

        .job-header i {
          font-size: 20px;
          color: #4f46e5;
        }

        .job-card p {
          margin-bottom: 6px;
          color: #374151;
          font-size: 14px;
        }

        .btn-indigo {
          background-color: #4f46e5;
          color: #fff;
          border-radius: 6px;
          padding: 6px 14px;
          font-size: 14px;
        }

        .btn-indigo:hover {
          background-color: #4338ca;
        }

        /* JOB DETAILS */
        .job-details-form {
          margin-top: 10px;
        }

        .job-details-item {
          border-bottom: 1px dashed #e5e7eb;
          padding-bottom: 6px;
        }

        .job-details-label {
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          margin-bottom: 2px;
        }

        .job-details-value {
          font-size: 14px;
          color: #111827;
          padding: 2px 0;
          border: none;
          background: transparent;
        }

        .job-details-value.highlight {
          color: #1d4ed8;
          font-weight: 600;
        }
      `}</style>
    </>
  )
}

/* REUSABLE DETAIL ROW */
const Detail = ({
  label,
  value,
  highlight = false
}: {
  label: string;
  value?: string;
  highlight?: boolean;
}) => (
  <div className="col-md-3 job-details-item">
    <div className="job-details-label">{label}</div>
    <div className={`job-details-value ${highlight ? "highlight" : ""}`}>
      {value || "—"}
    </div>
  </div>
);

export default VacancyCard;
